// Staff/coach-facing match-record card on the athlete detail page (Wave 2:
// tennis + wrestling). Read-only display with derived W-L / bonus summary;
// verified rows show a blue seal that staff/partners (canVerify) can toggle.
// Rendered only for match_record athletes.
import React from 'react';
import SealButton from './SealButton';
import useMatchRecordVerify from './useMatchRecordVerify';

const RESULT_GLYPH = (r) => (r === 'win' ? 'W' : r === 'loss' ? 'L' : '—');
const DISCIPLINE_LABEL = { singles: 'Singles', doubles: 'Doubles' };
const LEVEL_LABEL = { usta_national: 'USTA National', usta_sectional: 'USTA Sectional', itf: 'ITF', other: 'Other' };
const STYLE_LABEL = { folkstyle: 'Folkstyle', freestyle: 'Freestyle', greco: 'Greco-Roman' };
const METHOD_LABEL = { fall: 'Fall', tech_fall: 'Tech Fall', major_decision: 'Major Decision', decision: 'Decision', other: 'Other' };
const wl = (line) => `${(line && line.wins) || 0}–${(line && line.losses) || 0}`;
const ordinal = (n) => {
  if (n == null) return '';
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// A read-only ledger row with a leading chip, title, meta chips, and a seal.
function VerifyRow({ row, chip, chipKind, title, meta, canVerify, toggleVerify, busyId }) {
  return (
    <div className="ae-accolade">
      <div className="ae-accolade__body">
        <div className="ae-accolade__title">
          <span className={`ae-result-chip ae-result-chip--${chipKind}`}>{chip}</span>
          {title}
        </div>
        <div className="ae-accolade__meta">
          {meta.filter(Boolean).map((m, i) => <span key={i} className="ae-accolade__src">{m}</span>)}
        </div>
      </div>
      {canVerify ? (
        <SealButton
          className="ae-accolade__seal"
          state={row.verified ? 'verified' : 'unverified'}
          title={row.verified ? 'Verified — click to unverify' : 'Not verified — click to verify'}
          disabled={busyId === row.id}
          onToggle={() => toggleVerify(row)}
        />
      ) : (
        row.verified && <SealButton className="ae-accolade__seal" state="verified" title="Verified" disabled onToggle={() => {}} />
      )}
    </div>
  );
}

function TennisCard({ userId, matches, setMatches, records, canVerify, apiUrl, getConfig }) {
  const { toggleVerify, busyId } = useMatchRecordVerify({ userId, apiUrl, getConfig, setRows: setMatches, resource: 'tennis-matches' });
  if (!matches || matches.length === 0) return null;
  const groups = ['singles', 'doubles']
    .map(d => ({ d, rows: matches.filter(m => m.discipline === d) }))
    .filter(g => g.rows.length);

  return (
    <div className="ae-card ae-card--pad">
      <div className="ae-card__head">
        <h3 className="ae-card__title">Match Record</h3>
        {canVerify && <span className="ae-card__hint">Click a seal to verify</span>}
      </div>
      <div className="ae-record-summary">
        <div className="ae-record-tile"><span className="ae-record-tile__cap">Singles</span><span className="ae-record-tile__val">{wl(records?.singles?.career)}</span></div>
        <div className="ae-record-tile"><span className="ae-record-tile__cap">Doubles</span><span className="ae-record-tile__val">{wl(records?.doubles?.career)}</span></div>
      </div>
      <div className="ae-marks">
        {groups.map(g => (
          <div key={g.d} className="ae-mark-group">
            <div className="ae-mark-group__head">{DISCIPLINE_LABEL[g.d]}</div>
            {g.rows.map(m => (
              <VerifyRow
                key={m.id} row={m} chip={RESULT_GLYPH(m.result)} chipKind={m.result}
                title={m.opponent || 'Unknown opponent'}
                meta={[m.tournament_name, m.level && LEVEL_LABEL[m.level], m.round, m.match_date]}
                canVerify={canVerify} toggleVerify={toggleVerify} busyId={busyId}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function WrestlingCard({ userId, matches, setMatches, records, placements, setPlacements, canVerify, apiUrl, getConfig }) {
  const mv = useMatchRecordVerify({ userId, apiUrl, getConfig, setRows: setMatches, resource: 'wrestling/matches' });
  const pv = useMatchRecordVerify({ userId, apiUrl, getConfig, setRows: setPlacements, resource: 'wrestling/placements' });
  const hasMatches = matches && matches.length > 0;
  const hasPlacements = placements && placements.length > 0;
  if (!hasMatches && !hasPlacements) return null;

  const activeStyles = ['folkstyle', 'freestyle', 'greco'].filter(s => {
    const c = records && records[s] && records[s].career;
    return c && (c.wins || c.losses);
  });

  return (
    <div className="ae-card ae-card--pad">
      <div className="ae-card__head">
        <h3 className="ae-card__title">Wrestling</h3>
        {canVerify && <span className="ae-card__hint">Click a seal to verify</span>}
      </div>
      {activeStyles.length > 0 && (
        <div className="ae-record-summary">
          {activeStyles.map(s => {
            const c = records[s].career;
            return (
              <div key={s} className="ae-record-tile">
                <span className="ae-record-tile__cap">{STYLE_LABEL[s]}</span>
                <span className="ae-record-tile__val">{wl(c)}</span>
                <span className="ae-record-tile__sub">{Math.round((c.bonus_rate || 0) * 100)}% bonus</span>
              </div>
            );
          })}
        </div>
      )}
      {hasMatches && (
        <div className="ae-marks">
          <div className="ae-mark-group">
            <div className="ae-mark-group__head">Matches</div>
            {matches.map(m => (
              <VerifyRow
                key={m.id} row={m} chip={RESULT_GLYPH(m.result)} chipKind={m.result}
                title={m.opponent || 'Unknown opponent'}
                meta={[STYLE_LABEL[m.style], m.weight_class, m.method && METHOD_LABEL[m.method], m.event_name, m.event_date]}
                canVerify={canVerify} toggleVerify={mv.toggleVerify} busyId={mv.busyId}
              />
            ))}
          </div>
        </div>
      )}
      {hasPlacements && (
        <div className="ae-marks" style={{ marginTop: 12 }}>
          <div className="ae-mark-group">
            <div className="ae-mark-group__head">Placements</div>
            {placements.map(p => (
              <VerifyRow
                key={p.id} row={p} chip={ordinal(p.place) || '—'} chipKind="place"
                title={p.event_name || 'Event'}
                meta={[STYLE_LABEL[p.style], p.weight_class, p.event_date]}
                canVerify={canVerify} toggleVerify={pv.toggleVerify} busyId={pv.busyId}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MatchRecordCard(props) {
  const { sportSlug } = props;
  if (sportSlug === 'tennis') {
    return <TennisCard {...props} matches={props.tennisMatches} setMatches={props.setTennisMatches} records={props.tennisRecords} />;
  }
  if (sportSlug === 'wrestling') {
    return (
      <WrestlingCard
        {...props}
        matches={props.wrestlingMatches} setMatches={props.setWrestlingMatches}
        records={props.wrestlingRecords}
        placements={props.wrestlingPlacements} setPlacements={props.setWrestlingPlacements}
      />
    );
  }
  return null;
}
